import { AnimatePresence, motion } from "framer-motion";
import { MailIcon } from "lucide-react";
import { useState } from "react";

import client from "@/api/client";
import "./ContactModal.css";

interface ContactModalProps {
  onClose?: () => void;
}

export default function ContactModal({ onClose: _onClose }: ContactModalProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await client.post("/contact", {
        subject: subject.trim(),
        message: message.trim(),
        reply_email: replyEmail.trim() || undefined,
      });
      setSent(true);
      setSubject("");
      setMessage("");
      setReplyEmail("");
    } catch (apiError: unknown) {
      const detail =
        (apiError as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || "Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cm-root">
      {/* ── Left info panel ── */}
      <div className="cm-left">
        <div className="cm-glow" aria-hidden="true" />
        <div className="cm-left-content">
          <h2 className="cm-title">Get in touch</h2>
          <p className="cm-desc">
            Have a question about our services or need help? Fill out the form
            and we&rsquo;ll get back to you within 1 business day.
          </p>
          <div className="cm-info-list">
            <div className="cm-info-item">
              <div className="cm-info-icon">
                <MailIcon size={16} />
              </div>
              <div>
                <p className="cm-info-label">Email</p>
                <p className="cm-info-value">fgandolfo@tozenlabs.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="cm-right">
        <AnimatePresence mode="wait">
          {sent ? (
            <motion.div
              key="success"
              className="cm-success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="cm-success-icon">
                <svg viewBox="0 0 22 22">
                  <polyline points="3,11.5 8.5,17 19,6" />
                </svg>
              </div>
              <p className="cm-success-title">Message sent</p>
              <p className="cm-success-body">We&rsquo;ll get back to you soon.</p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              className="cm-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="cm-field">
                <label htmlFor="cm-reply-email">
                  Your email{" "}
                  <span className="cm-field-optional">(optional)</span>
                </label>
                <input
                  id="cm-reply-email"
                  type="email"
                  autoComplete="email"
                  value={replyEmail}
                  onChange={(e) => setReplyEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="cm-field">
                <label htmlFor="cm-subject">Subject</label>
                <input
                  id="cm-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="How can we help?"
                  maxLength={200}
                />
              </div>

              <div className="cm-field">
                <label htmlFor="cm-message">Message</label>
                <textarea
                  id="cm-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Write your message…"
                  maxLength={4000}
                  required
                />
              </div>

              {error ? <p className="error-text">{error}</p> : null}

              <button type="submit" className="cm-submit" disabled={loading}>
                {loading ? "Sending…" : "Send message"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
